/**
 * IndexedDB cache for snapshot state bytes.
 *
 * Enables offline restore/diff of historical snapshots that have already
 * been flushed to the server — the server stores them in entity_snapshots,
 * but by default the client only keeps metadata (id, name, timestamp, etc.)
 * in entityStore. Without this cache, opening the diff view offline for a
 * previously-fetched snapshot would fail with "Failed to load snapshot".
 *
 * Semantics:
 * - Snapshots are immutable server-side, so cached bytes never go stale.
 * - Cache is write-through: SnapshotService.fetchSnapshotState writes the
 *   bytes here after a successful WS previewOnly fetch.
 * - Read path is tier 2 of fetchSnapshotState, sitting between pendingOps
 *   (tier 1, offline-created) and the WS op (tier 3, last resort).
 * - LRU-ish eviction: each entry tracks lastAccess; when the total count
 *   exceeds MAX_ENTRIES we drop the oldest. Simple count cap instead of
 *   byte budget because snapshot size is bounded by the 10 MB frame limit
 *   and realistic counts stay small.
 * - Graceful degradation: if IDB is unavailable (private browsing, SSR),
 *   every operation is a no-op and tier-3 WS fetch carries the load.
 */

const IDB_NAME = 'nula-snapshot-states';
const IDB_STORE = 'kv';
const IDB_VERSION = 1;
const MAX_ENTRIES = 100;

interface CacheEntry {
  bytes: Uint8Array;
  lastAccess: number;
}

const IDB_AVAILABLE = typeof indexedDB !== 'undefined';

// Cached open promise — keeps a single long-lived connection rather than
// opening a fresh handle on every read/write. Diff views and snapshot
// preview flows can issue several concurrent reads; without the cache,
// each call opens its own DB and the touch() update in readSnapshotState
// races against the next read.
let cachedDb: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (cachedDb) return cachedDb;
  cachedDb = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onclose = () => {
        // Connection died — drop the cache so the next call reopens.
        if (cachedDb === thisPromise) cachedDb = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      if (cachedDb === thisPromise) cachedDb = null;
      reject(req.error);
    };
  });
  const thisPromise = cachedDb;
  return cachedDb;
}

/**
 * Read cached bytes for a snapshot id. Returns null if not cached or if
 * IDB is unavailable. Touches the entry's lastAccess so it stays warm.
 */
export async function readSnapshotState(id: string): Promise<Uint8Array | null> {
  if (!IDB_AVAILABLE) return null;
  try {
    const db = await openDb();
    const entry = await new Promise<CacheEntry | undefined>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(id);
      req.onsuccess = () => resolve(req.result as CacheEntry | undefined);
      req.onerror = () => reject(req.error);
    });
    if (!entry) return null;
    // Touch lastAccess asynchronously — we don't need to block the read on it.
    void touch(db, id, entry);
    return entry.bytes;
  } catch {
    return null;
  }
}

/**
 * Write snapshot bytes to the cache. Runs eviction if the entry count
 * exceeds MAX_ENTRIES. No-op if IDB is unavailable or on any error —
 * the cache is best-effort and must never block a successful fetch.
 */
export async function writeSnapshotState(id: string, bytes: Uint8Array): Promise<void> {
  if (!IDB_AVAILABLE) return;
  try {
    const db = await openDb();
    const entry: CacheEntry = { bytes, lastAccess: Date.now() };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(entry, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    await evictIfOverLimit(db);
  } catch {
    /* best-effort */
  }
}

/**
 * Remove a single cached entry. Used when a snapshot is deleted so we
 * don't hold stale bytes indefinitely.
 */
export async function deleteSnapshotState(id: string): Promise<void> {
  if (!IDB_AVAILABLE) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Drop the entire cache. Primarily for tests and for handling sign-out
 * flows where we want to forget all cached content.
 */
export async function clearSnapshotStateCache(): Promise<void> {
  if (!IDB_AVAILABLE) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}

/** Internal: bump lastAccess on a hit without blocking the read. */
async function touch(db: IDBDatabase, id: string, entry: CacheEntry): Promise<void> {
  try {
    entry.lastAccess = Date.now();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(entry, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Internal: when the cache has more than MAX_ENTRIES entries, drop the
 * oldest by lastAccess. Runs after every write so the cache self-trims.
 */
async function evictIfOverLimit(db: IDBDatabase): Promise<void> {
  const entries = await new Promise<{ key: IDBValidKey; lastAccess: number }[]>((resolve, reject) => {
    const out: { key: IDBValidKey; lastAccess: number }[] = [];
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const value = cursor.value as CacheEntry;
        out.push({ key: cursor.key, lastAccess: value.lastAccess });
        cursor.continue();
      } else {
        resolve(out);
      }
    };
    req.onerror = () => reject(req.error);
  });

  if (entries.length <= MAX_ENTRIES) return;

  // Evict entries.length - MAX_ENTRIES oldest.
  entries.sort((a, b) => a.lastAccess - b.lastAccess);
  const victims = entries.slice(0, entries.length - MAX_ENTRIES).map((e) => e.key);

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    for (const key of victims) store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Exposed for tests so they can assert the cap. */
export const SNAPSHOT_STATE_CACHE_MAX_ENTRIES = MAX_ENTRIES;
